// NoteBand — a note of a moment (Moment ADR-040 D3) drawn on the Moment swatch.
// A note narrates a step the model does not describe: it is not a node, it is
// never walked, and it asserts nothing, so it carries no handlers and no run
// state. The full text is the title; the band shows up to three lines.

import { NDD_PALETTE, INK_DARK } from '../../services/styleResolver.ts';
import type { NotePosition } from './layoutEngine.ts';

const LINE_CHARS = 30;
const MAX_LINES = 3;

/** Greedy word wrap into at most MAX_LINES lines; the last line takes the ellipsis. */
function wrapNote(text: string): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = '';
  for (const word of words) {
    const next = cur ? `${cur} ${word}` : word;
    if (next.length <= LINE_CHARS || !cur) {
      cur = next;
    } else {
      lines.push(cur);
      cur = word;
    }
  }
  if (cur) lines.push(cur);
  if (lines.length <= MAX_LINES) return lines;
  const kept = lines.slice(0, MAX_LINES);
  const last = kept[MAX_LINES - 1];
  kept[MAX_LINES - 1] = (last.length > LINE_CHARS - 1 ? last.slice(0, LINE_CHARS - 1) : last) + '…';
  return kept;
}

export default function NoteBand({ note }: { note: NotePosition }) {
  const lines = wrapNote(note.text);
  const lineH = 10.5;
  const top = note.y + (note.h - lines.length * lineH) / 2 + lineH / 2;
  return (
    <g data-testid="note-band" data-frame={note.frameId} aria-label={`Note: ${note.text}`}>
      <title>{note.text}</title>
      <rect
        x={note.x + 6}
        y={note.y}
        width={note.w}
        height={note.h}
        rx={5}
        fill={NDD_PALETTE.moment.fill}
        stroke="rgba(26, 26, 26, 0.35)"
        strokeWidth={1}
      />
      {lines.map((line, i) => (
        <text
          key={i}
          x={note.x + 6 + 9}
          y={top + i * lineH}
          dominantBaseline="middle"
          fill={INK_DARK}
          fontSize="9"
          fontStyle="italic"
          fontFamily="'IBM Plex Sans', sans-serif"
        >
          {line}
        </text>
      ))}
    </g>
  );
}
